import { ThanksPrintConfig } from '@shared/api/giftItemBrand/giftItemBrand.interface';
import { GiftThanksPrintDto } from '@shared/api/giftOrder/giftOrder.interface';
import { Image } from '@shared/api/media/media.interface';
import { Member } from '@shared/api/member/member.interface';
import { Gender } from '@shared/types';
import { getReverseType } from '@shared/utils/memberUtils';

/** 감사글 안에서 고인/상중 표현으로 치환되는 토큰 */
export const THANKS_DECEASED_TOKEN = '###';

/** 발신 표현 옵션 (기본: 배상) */
export const THANKS_SENDER_SUFFIXES = ['배상', '드림', '올림'] as const;
export type ThanksSenderSuffix = typeof THANKS_SENDER_SUFFIXES[number];

/** 상주 정보가 없을 때 이름 fallback */
export const THANKS_FALLBACK_NAME = 'OOO';

/**
 * reverseType(고인의 상주에 대한 관계 용어) → 구어체 호칭.
 * 예: 부친→아버지, 모친→어머니, 조부→할아버지, 조모→할머니 ...
 */
const REVERSE_TYPE_TO_WORD: Record<string, string> = {
  부친: '아버지',
  모친: '어머니',
  조부: '할아버지',
  조모: '할머니',
  외조부: '외할아버지',
  외조모: '외할머니',
  시부: '시아버지',
  시모: '시어머니',
  시조부: '시할아버지',
  시조모: '시할머니',
  빙조부: '처조부',
  빙조모: '처조모',
  장인: '장인어른',
  장모: '장모님',
  빙부: '장인어른',
  빙모: '장모님',
  부군: '남편',
  부인: '아내',
  배우자: '배우자',
  형제: '형제',
};

/**
 * ### 표현 생성 — relationType(상주의 고인에 대한 관계) + 고인 성별로 계산.
 * - 관계가 확인되면 "저희 {구어체 호칭} 상중" (예: "저희 아버지 상중")
 * - 관계를 만들 수 없으면 "상중" (저희도 제외)
 * - 상주(relationType 미설정)는 아들(자)로 간주해 부친/모친 계산
 */
export const getThanksDeceasedPhrase = (
  relationType: string | undefined,
  deceasedSex: Gender | undefined,
): string => {
  if (!deceasedSex) return '상중';
  const rel = relationType?.trim() || '자';
  const reverseType = getReverseType(rel, deceasedSex);
  const word = reverseType ? REVERSE_TYPE_TO_WORD[reverseType] ?? reverseType : '';
  return word ? `저희 ${word} 상중` : '상중';
};

/** 상주 이름 (없으면 OOO) */
export const getThanksSenderName = (member?: Pick<Member, 'fullName'> | null): string =>
  member?.fullName?.trim() || THANKS_FALLBACK_NAME;

/** 프리셋의 ### 토큰을 "저희 OOO 상중" 표현으로 치환 */
export const fillThanksMessage = (
  template: string,
  relationType: string | undefined,
  deceasedSex: Gender | undefined,
): string =>
  template
    .split(THANKS_DECEASED_TOKEN)
    .join(getThanksDeceasedPhrase(relationType, deceasedSex));

/** 이름 + 발신표현 합성 ("홍길동 배상") */
export const composeThanksSender = (name: string, suffix: ThanksSenderSuffix): string =>
  `${name.trim() || THANKS_FALLBACK_NAME} ${suffix}`;

/** 주문에 보낼 감사인쇄 선택값 (giftThanksPrintAtom과 호환되는 구조) */
export interface ThanksPrintSelectionInput {
  message: string;
  senderName: string;
  senderSuffix: ThanksSenderSuffix;
  brandId?: string;
  templateId?: string;
}

/**
 * 감사인쇄 선택값 + 주문 대표 브랜드 설정 → 주문 전송용 thanksPrint DTO.
 * - 감사글(message)이 비어있으면(미작성) undefined → 주문에 thanksPrint를 넣지 않음.
 * - templateId는 같은 브랜드의 저장된 디자인을 쓰되, 매칭 안 되면 첫 노출 템플릿으로 보정.
 * - senderName은 발신표현까지 합성("홍길동 배상")해 미리보기와 동일하게 인쇄되도록 한다.
 */
export const buildThanksPrintDto = (
  selection: ThanksPrintSelectionInput | null,
  config: ThanksPrintConfig<Image>,
  brandId: string,
): GiftThanksPrintDto | undefined => {
  const message = selection?.message?.trim();
  if (!selection || !message) return undefined;

  const templates = (config.templates ?? [])
    .filter((t) => t.enabled !== false)
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const firstTemplate = templates[0];
  if (!firstTemplate) return undefined;

  const matched =
    selection.brandId === brandId && selection.templateId
      ? templates.find((t) => t._id === selection.templateId)
      : undefined;
  const templateId = (matched ?? firstTemplate)._id;
  if (!templateId) return undefined;

  const { name, suffix } = parseThanksSender(selection.senderName, selection.senderSuffix);
  return { templateId, message, senderName: composeThanksSender(name, suffix) };
};

/** 유효한 발신표현인지 */
export const isThanksSenderSuffix = (value: unknown): value is ThanksSenderSuffix =>
  THANKS_SENDER_SUFFIXES.includes(value as ThanksSenderSuffix);

/**
 * 저장된 발신자 값을 이름/표현으로 분리·정규화.
 * - suffix가 유효하지 않으면 기본 '배상'
 * - 레거시 합성값("홍길동 배상")처럼 이름에 표현이 붙어있으면 떼어냄
 *   (undefined가 붙는 "홍길동 배상 undefined" 류 깨짐 방지)
 */
export const parseThanksSender = (
  storedName: string | undefined,
  storedSuffix: unknown,
): { name: string; suffix: ThanksSenderSuffix } => {
  let name = (storedName ?? '').trim();
  let suffix = isThanksSenderSuffix(storedSuffix) ? storedSuffix : undefined;
  for (const s of THANKS_SENDER_SUFFIXES) {
    if (name.endsWith(` ${s}`)) {
      name = name.slice(0, name.length - s.length).trim();
      suffix = suffix ?? s;
      break;
    }
  }
  return { name: name || THANKS_FALLBACK_NAME, suffix: suffix ?? '배상' };
};

/** 감사글 입력 제한 */
export const THANKS_MESSAGE_MAX_LINES = 4;
export const THANKS_MESSAGE_MAX_CHARS = 100; // 줄바꿈 제외 글자수

/** 줄바꿈 제외 글자수 */
export const thanksMessageCharCount = (value: string): number =>
  value.replace(/\n/g, '').length;

/**
 * 감사글을 4줄 / 100자(줄바꿈 제외) 이내로 제한.
 * 초과분은 잘라내되 줄바꿈 위치는 보존.
 */
export const clampThanksMessage = (value: string): string => {
  const lines = value.split('\n').slice(0, THANKS_MESSAGE_MAX_LINES);
  let count = 0;
  let result = '';
  for (const ch of lines.join('\n')) {
    if (ch === '\n') {
      result += ch;
      continue;
    }
    if (count >= THANKS_MESSAGE_MAX_CHARS) break;
    result += ch;
    count += 1;
  }
  return result;
};

/**
 * 감사글 프리셋 (### 는 사용 시 member 표현으로 치환).
 * 줄바꿈은 인쇄 레이아웃에 맞춰 유지됨(break-keep + pre-line).
 */
export const THANKS_MESSAGE_PRESETS: string[] = [
  `바쁘신 와중에도 불구하고\n${THANKS_DECEASED_TOKEN}에\n위로해주셔서 감사합니다.`,
  `바쁘신 와중에도 친히\n${THANKS_DECEASED_TOKEN}에\n위로해 주셔서 감사합니다.`,
  `바쁘신 와중에도\n${THANKS_DECEASED_TOKEN}에\n귀한 발걸음 해 주신 덕분에\n큰 힘이 되었습니다.`,
  `조문과 부의로 위로해 주신 것에\n깊은 감사의 말씀을 드립니다.`,
  `${THANKS_DECEASED_TOKEN}에 따뜻한 마음으로\n위로해 주셔서 진심으로 감사합니다.\n덕분에 슬픔을 이겨낼 수 있었습니다.`,
];
